<template>
  <PageContainer title="Alunos cadastrados">
    <div v-if="role === 1 || canEmitirLadv" slot="header" class="header">
      <div v-if="role === 1" class="badges">
        <Badge :count="badges.ativos" text="ativos" color="success" />
        <Badge :count="badges.inativos" text="inativos" color="danger" />
      </div>

      <div class="header-buttons">
        <Button v-if="canEmitirLadv" type="button" @handle:click="toLadv()" text="Novo aluno com LADV"
          color="primary">
          <CardAccountDetailsOutline :size="18" />
        </Button>

        <Button v-if="role === 1" type="button" @handle:click="openExportModal" text="Exportar" color="primary"
          :fill="false" :disabled="loading">
          <TrayArrowDown :size="18" />
        </Button>
      </div>
    </div>

    <div v-if="!loaded" class="d-flex justify-content-center">
      <component :animation-duration="1000" :is="'SpringSpinner'" :color="'#1C2D5A'" :size="80" class="mx-2">
      </component>
    </div>

    <Box v-else class="alunos-container" space="medium">
      <form @submit.prevent="handleSubmit">
        <div class="inputs">
          <div class="inputs-row">
            <Select v-if="role === 6" class="input-full" :options="schemaOptions" legend="Autoescola"
              placeholder="Selecione" :modelValue="schema" @update:modelValue="$event => (this.schema = $event)" />

            <Input class="input-full" :modelValue="nomeAluno" @update:modelValue="$event => (this.nomeAluno = $event)"
              legend="Aluno" placeholder="Nome aluno" :disabled="loading" />

            <Input class="input-full" mask="###.###.###-##" :modelValue="cpf"
              @update:modelValue="$event => (this.cpf = $event)" legend="CPF" placeholder="CPF" :disabled="loading" />

            <Input class="input-full" :modelValue="renach" @update:modelValue="$event => (this.renach = $event)"
              legend="Renach" placeholder="Renach" :disabled="loading" />
          </div>
          <div class="inputs-row">
            <SingleDate class="input-processo" legend="Início do processo" :modelValue="inicioProcesso"
              @update:modelValue="$event => (this.inicioProcesso = $event)" />

            <div class="buttons">
              <Button type="button" @handle:click="clearInputs" text="Limpar" :fill="false" color="danger"
                :disabled="loading">
                <CloseCircleOutline :size="18" />
              </Button>

              <Button type="submit" text="Consultar" color="primary" :disabled="loading">
                <Magnify :size="18" />
              </Button>
            </div>
          </div>
        </div>
      </form>

      <div v-if="loading" class="d-flex justify-content-center">
        <component :animation-duration="1000" :is="'SpringSpinner'" :color="'#1C2D5A'" :size="80" class="mx-2">
        </component>
      </div>

      <div v-else-if="alunos.length > 0 && !loading" class="table-container">
        <Paginate :total-pages="paginationData.lastPage" :current-page="paginationData.currentPage"
          @page-changed="fetchAlunos" />

        <Table :headers="headers">
          <tr v-for="aluno in alunos" :key="aluno.id_aluno">
            <td>
              <Badge v-if="aluno.flg_ativo" text="Ativo" color="success" />
              <Badge v-else text="Inativo" color="danger" />
            </td>
            <td>{{ aluno.nome }}</td>
            <td>{{ utils.formatarCPF(aluno.cpf) }}</td>
            <template v-if="role === 1">
              <td>{{ utils.formatOnlyDate(aluno.inicio_processo) }}</td>
            </template>
            <template v-else>
              <td v-if="aluno.ladv_inicio">{{ utils.formatOnlyDate(aluno.ladv_inicio) }}</td>
              <td v-else>-</td>
              <td v-if="aluno.ladv_fim">{{ utils.formatOnlyDate(aluno.ladv_fim) }}</td>
              <td v-else>-</td>
            </template>
            <td>
              <div class="actions">
                <div v-if="aluno.categoria">
                  <IconButton @handle:click="openInfoModal(aluno.id_aluno)" color="secondary"
                    title="Ver informações do aluno" :disabled="loadingActions">
                    <InformationOutline :size="19" />
                  </IconButton>
                </div>
                <div v-if="canEmitirLadv">
                  <IconButton @handle:click="toLadv(aluno.id_aluno)" color="primary" title="Emitir LADV"
                    :disabled="loadingActions">
                    <CardAccountDetailsOutline :size="19" />
                  </IconButton>
                </div>
                <template v-if="role === 1">
                  <div v-if="aluno.flg_ativo">
                    <IconButton @handle:click="updateStatus(aluno.id_aluno, 'desativar')" color="danger"
                      title="Desativar aluno" :disabled="loadingActions">
                      <LockOutline :size="19" />
                    </IconButton>
                  </div>
                  <div v-else>
                    <IconButton @handle:click="updateStatus(aluno.id_aluno, 'ativar')" color="success"
                      title="Ativar aluno" :disabled="loadingActions">
                      <LockOpenOutline :size="19" />
                    </IconButton>
                  </div>
                  <div>
                    <IconButton @handle:click="updateAluno(aluno.id_aluno)" color="primary" title="Atualizar aluno"
                      :disabled="!aluno.flg_ativo || loadingActions">
                      <CloudRefreshVariantOutline :size="19" />
                    </IconButton>
                  </div>
                  <div v-if="aluno.renach">
                    <IconButton @handle:click="consumirCreditos(aluno.renach)" color="warning"
                      title="Consumir créditos" :disabled="loadingActions">
                      <CurrencyUsd :size="19" />
                    </IconButton>
                  </div>
                </template>
              </div>
            </td>
          </tr>
        </Table>
      </div>
    </Box>

    <ExportModal ref="exportModal" @submit:export="submitExport" :loading="exportLoading" />

    <InfoModal ref="infoModal" />
  </PageContainer>
</template>

<script>
import * as spinners from 'epic-spinners'
import urlApi from '../../router/urlApi'
import urlApiV2 from '../../router/urlApiV2'
import PageContainer from 'core/PageContainer'
import Box from 'core/Box'
import Button from 'core/Button'
import Input from 'core/Input'
import SingleDate from 'core/SingleDate'
import Badge from 'core/Badge'
import Table from 'core/Table'
import Paginate from 'core/Paginate'
import IconButton from 'core/IconButton'
import Select from 'core/Select'
import utils from '../../services/utils'
import LockOutline from 'icons/LockOutline'
import LockOpenOutline from 'icons/LockOpenOutline'
import InformationOutline from 'icons/InformationOutline'
import CloudRefreshVariantOutline from 'icons/CloudRefreshVariantOutline'
import TrayArrowDown from 'icons/TrayArrowDown'
import CloseCircleOutline from 'icons/CloseCircleOutline'
import Magnify from 'icons/Magnify'
import CurrencyUsd from 'icons/CurrencyUsd'
import CardAccountDetailsOutline from 'icons/CardAccountDetailsOutline'
import { useAuth } from '@/composables/useAuth'
import ExportModal from '../export/ExportModal'
import _export from '../../services/export'
import InfoModal from './InfoModal'

export default {
  name: 'ListagemAlunos',
  components: {
    ...spinners,
    LockOutline,
    LockOpenOutline,
    InformationOutline,
    CloudRefreshVariantOutline,
    TrayArrowDown,
    CloseCircleOutline,
    Magnify,
    CurrencyUsd,
    CardAccountDetailsOutline,

    PageContainer,
    Box,
    Button,
    Input,
    SingleDate,
    Badge,
    Table,
    Paginate,
    IconButton,
    Select,
    ExportModal,
    InfoModal,
  },
  data() {
    return {
      role: Number(localStorage.id_role),
      canEmitirLadv: useAuth().canEmitirLadv,
      schema: '',
      nomeAluno: '',
      cpf: '',
      renach: '',
      inicioProcesso: '',
      filters: null,
      badges: {
        ativos: '',
        inativos: '',
      },
      paginationData: {
        currentPage: 1,
        lastPage: 0,
        total: 0,
      },
      schemaOptions: [],
      alunos: [],
      loading: false,
      loadingActions: false,
      exportLoading: false,
      loaded: false,
      utils: utils,
    }
  },
  async created() {
    if (this.role === 1) {
      await this.loadAlunosStatus()
    } else {
      await this.fetchCfcs()
    }

    this.loaded = true
  },
  methods: {
    async fetchCfcs() {
      await this.$http.get(`${process.env.VUE_APP_URL_API_V2}/autoescola/todas`)
        .then((res) => {
          this.schemaOptions = res.body.dados.map(cfc => ({ value: cfc.tenant_schema, text: cfc.nome_fantasia }))
        })
        .catch((error) => {
          this.$toasted.show('Erro!' + error.body.mensagem, {
            position: 'top-right',
          })
        })
    },
    async loadAlunosStatus() {
      await this.$http.get(`${urlApiV2}/alunos/consultar/status-count`)
        .then(response => {
          this.badges = response.body.dados
        })
        .catch(err => {
          this.toast(err.body.mensagem)
        })
    },
    async fetchAlunos(page = 1) {
      this.loading = true

      const params = {
        page,
      }

      if (this.role === 6 && this.schema) params.schema = this.schema
      if (this.nomeAluno) params.nome = this.nomeAluno
      if (this.cpf) params.cpf = this.cpf
      if (this.renach) params.renach = this.renach
      if (this.inicioProcesso) params.inicioProcesso = this.inicioProcesso

      this.filters = params

      await this.$http.get(`${urlApiV2}/alunos`, {
        params,
      })
        .then(response => {


          const data = response.body.dados

          if (!data.data.length) {
            this.$toasted.show('Sem resultados para sua pesquisa.', {
              position: 'top-right',
            })
          }

          this.alunos = data.data

          this.paginationData = {
            currentPage: data.current_page,
            lastPage: data.last_page,
            total: data.total,
          }
        })
        .catch(err => {
          this.toast('Erro ao buscar alunos.')
        })
        .finally(() => {
          this.loading = false
        })
    },
    async handleSubmit() {
      if (this.validateInputs()) {
        this.fetchAlunos()
      }
    },
    async updateStatus(id, status) {
      this.loadingActions = true

      await this.$http.patch(`${urlApi}/alunos/${id}/${status}`)
        .then(async response => {
          this.toast('Status atualizado!')

          await Promise.all([
            this.fetchAlunos(),
            this.loadAlunosStatus(),
          ])
        })
        .catch(err => {
          this.toast('Erro ao atualizar status do aluno.')
        })
        .finally(() => {
          this.loadingActions = false
        })
    },
    async updateAluno(id) {
      this.loadingActions = true

      await this.$http.get(`${urlApi}/getran/atualizar/aluno/${id}`)
        .then(async response => {
          this.toast('Aluno atualizado!')

          await new Promise.all([
            this.fetchAlunos(),
            this.loadAlunosStatus(),
          ])
        })
        .catch(err => {
          this.toast(err.body.dados)
        })
        .finally(() => {
          this.loadingActions = false
        })
    },
    async consumirCreditos(renach) {
      this.loadingActions = true

      await this.$http.post(`${urlApiV2}/creditos/debit-credit/pratico`, { renach })
        .then(response => {
          this.$toasted.show('Créditos consumidos com sucesso!', {
            position: 'top-right',
          })
        })
        .catch(err => {
          const mensagem = err.body && err.body.message ? err.body.message : 'Erro ao consumir créditos.'
          this.$toasted.show(mensagem, {
            position: 'top-right',
          })
        })
        .finally(() => {
          this.loadingActions = false
        })
    },
    async submitExport(e) {
      this.exportLoading = true

      if (e.format === 'pdf') {
        await this.$http.get(`${urlApi}/alunos/pdf/${e.initialDate}/${e.finalDate}`)
          .then(response => {
            _export.downloadPDF(response.body.dados, "alunos")
          })
          .catch((err) => {
            this.toast("Erro ao exportar o arquivo.")
          })
          .finally(() => {
            this.exportLoading = false
          })
      } else {
        await this.$http.get(`${urlApi}/alunos/xlsx/${e.initialDate}/${e.finalDate}`)
          .then(response => {
            _export.downloadCSV(response.body.dados, "alunos")
          })
          .catch((err) => {
            this.toast("Erro ao exportar o arquivo.")
          })
          .finally(() => {
            this.exportLoading = false
          })
      }

      this.$refs.exportModal.close()
    },
    validateInputs() {
      if (this.cpf && this.cpf.length < 11) {
        this.toast('CPF Inválido.')

        return false
      }

      if (this.role === 6 && !this.schema) {
        this.toast('Selecione uma autoescola.')

        return false
      }

      return true
    },
    toLadv(idAluno) {
      this.$router.push({ name: 'emissao-ladv', params: idAluno ? { idAluno } : {} })
    },
    openExportModal() {
      this.$refs.exportModal.open()
    },
    openInfoModal(idAluno) {
      this.$refs.infoModal.open(idAluno)
    },
    clearInputs() {
      this.schema = ''
      this.nomeAluno = ''
      this.cpf = ''
      this.renach = ''
      this.inicioProcesso = ''
    },
    toast(text) {
      this.$toasted.show(text, {
        icon: 'fa-times',
        position: 'top-right',
        duration: 5000,
        fullWidth: false
      })
    },
  },
  computed: {
    headers() {
      if (this.role === 1) {
        return ['Status', 'Aluno', 'CPF', 'Início do processo', 'Ações']
      }

      return ['Status', 'Aluno', 'CPF', 'LADV início', 'LADV fim', 'Ações']
    }
  }
}
</script>

<style lang="scss" scoped>
.alunos-container {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.header {
  align-self: flex-end;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1.5rem;

  .badges,
  .header-buttons {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
  }
}

form {
  display: flex;
  flex-direction: column;
  gap: 2.5rem;

  .inputs {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;

    .inputs-row {
      display: flex;
      flex-wrap: wrap;
      gap: 1.5rem;

      .input-full {
        flex: 1;
      }
    }
  }

  .buttons {
    align-self: flex-end;

    display: flex;
    gap: 0.5rem;
  }
}

.table-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;

  .pagination-container {
    align-self: flex-end;
  }

  .table-container {
    .actions {
      display: flex;
      align-items: center;

      div {
        padding: 0 0.5rem;
        display: flex;
        justify-content: center;

        &:not(:first-child) {
          border-left: 1px solid #ECECEC;
        }

        &:not(:last-child) {
          border-right: 1px solid #ECECEC;
        }
      }
    }
  }
}
</style>
